import React, { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import RemoveIcon from '@material-ui/icons/Remove'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import Dialog, { DialogActions, DialogActionButton, DialogContent, DialogTitle } from '../Dialog'
import IconButton from '../IconButton'
import Slider from '../Slider'
import CircularProgress from '../CircularProgress'

const useStyles = makeStyles<Theme>((theme) => ({
  dialogPaper: {
    maxWidth: 500,
    minWidth: 400,
    [theme.breakpoints.down('xs')]: {
      minWidth: 'unset'
    }
  },
  content: {
    '&.MuiDialogContent-root': {
      padding: 0
    }
  },
  cropContainer: {
    position: 'relative',
    width: '100%',
    height: 350,
    backgroundColor: theme.palette.grey[100],
    [theme.breakpoints.down('xs')]: {
      height: 280
    }
  },
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1.5)
  },
  sliderIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20
  },
  slider: {
    flex: 1
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100]
  }
}))

export interface ProfilePhotoCropDialogProps {
  open: boolean
  imageFile: File | null
  onConfirm: (croppedFile: File) => void
  onCancel: () => void
  onError?: (message: string) => void
}

function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<File> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => {
      const canvas = document.createElement('canvas')
      canvas.width = pixelCrop.width
      canvas.height = pixelCrop.height
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Could not get canvas context'))
        return
      }

      // Fill white background so transparent PNGs and out-of-bounds areas (when zoomed out below 1) render white
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, pixelCrop.width, pixelCrop.height)

      // Compute the intersection between the requested crop region and the image bounds.
      // pixelCrop is in image-pixel coordinates and may extend outside the image when restrictPosition is false.
      const naturalW = image.naturalWidth
      const naturalH = image.naturalHeight
      const sx = Math.max(0, pixelCrop.x)
      const sy = Math.max(0, pixelCrop.y)
      const sw = Math.min(naturalW, pixelCrop.x + pixelCrop.width) - sx
      const sh = Math.min(naturalH, pixelCrop.y + pixelCrop.height) - sy

      if (sw > 0 && sh > 0) {
        const dx = sx - pixelCrop.x
        const dy = sy - pixelCrop.y
        ctx.drawImage(image, sx, sy, sw, sh, dx, dy, sw, sh)
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'))
            return
          }
          const file = new File([blob], 'avatar.jpeg', { type: 'image/jpeg' })
          resolve(file)
        },
        'image/jpeg',
        0.95
      )
    })
    image.addEventListener('error', () => reject(new Error('Failed to load image')))
    image.src = imageSrc
  })
}

function ProfilePhotoCropDialog({ open, imageFile, onConfirm, onCancel, onError }: ProfilePhotoCropDialogProps) {
  const classes = useStyles()
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [minZoom, setMinZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(false)

  React.useEffect(() => {
    if (imageFile) {
      // Clear previous image immediately to prevent stale flash on re-open
      setImageSrc(null)
      setImageLoading(true)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setMinZoom(1)

      const reader = new FileReader()
      reader.addEventListener('load', () => {
        const result = reader.result as string
        // Compute a min zoom that lets the longer side fit fully inside the square crop area
        const probe = new Image()
        probe.addEventListener('load', () => {
          const ratio = Math.min(probe.width, probe.height) / Math.max(probe.width, probe.height)
          setMinZoom(ratio)
          setImageSrc(result)
          setImageLoading(false)
        })
        probe.addEventListener('error', () => {
          setImageSrc(result)
          setImageLoading(false)
        })
        probe.src = result
      })
      reader.addEventListener('error', () => {
        setImageLoading(false)
        onError?.('Unable to read image file')
      })
      reader.readAsDataURL(imageFile)
    } else {
      setImageSrc(null)
      setImageLoading(false)
    }
  }, [imageFile])

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels)
      onConfirm(croppedFile)
    } catch (e) {
      console.error('Error cropping image:', e)
      onError?.('Unable to crop image')
    }
  }

  return (
    <Dialog open={open} onClose={onCancel} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        }
      >
        Crop Photo
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.cropContainer}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={3}
              aspect={1}
              cropShape="round"
              showGrid={false}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
          {imageLoading && (
            <div className={classes.loadingOverlay}>
              <CircularProgress />
            </div>
          )}
        </div>
        <div className={classes.sliderContainer}>
          <RemoveIcon className={classes.sliderIcon} />
          <div className={classes.slider}>
            <Slider value={zoom} min={minZoom} max={3} step={0.05} onChange={(_e, value) => setZoom(value as number)} />
          </div>
          <AddIcon className={classes.sliderIcon} />
        </div>
      </DialogContent>
      <DialogActions>
        <DialogActionButton onClick={handleConfirm} disabled={!imageSrc || imageLoading}>
          Save
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

export default ProfilePhotoCropDialog
